// ESCENARIO GLOBAL — REAL (datos/laboratorio) vs. DEMOS de venta (frontend-only).
// FUENTE ÚNICA MUTABLE del estado DEMO: `demoState` (alarmas + detalles + eventos). Todas las
// superficies (Central, Radar, Bitácora, Persona) leen de aquí → sin divergencia.
// Reglas duras:
//  - Sin persistencia: SIEMPRE se arranca en REAL; recargar resetea a REAL.
//  - Al entrar a un demo: guard de escrituras ON de forma SÍNCRONA; al volver a REAL: OFF inmediato.
//  - Las acciones demo (tomar/liberar/cerrar/…) se resuelven LOCALES, jamás llegan al backend.
import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { buildDemo, projectDemo } from "@/lib/demo/scenarios";
import { refrescarDia1, aplicarAccion, aplicarDesenlace, aplicarGestion, aplicarSeguimiento } from "@/lib/demo/dia1Demo";
import { setDemoGuard } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const VALID = ["real", "demo_inst", "demo_resid"];
const ScenarioCtx = createContext(null);
const clone = (o) => JSON.parse(JSON.stringify(o));
const initDemo = (key) => buildDemo(key);

export function ScenarioProvider({ children }) {
  const { user } = useAuth();
  const [scenario, setScenarioState] = useState("real");
  const [demoState, setDemoState] = useState(null);

  const setScenario = useCallback((s) => {
    const v = VALID.includes(s) ? s : "real";
    // Guard SÍNCRONO (no depende de un useEffect posterior).
    if (v === "real") { setDemoGuard(false); setDemoState(null); }
    else { setDemoGuard(true); setDemoState(initDemo(v)); }
    setScenarioState(v);
  }, []);

  const resetToReal = useCallback(() => { setDemoGuard(false); setDemoState(null); setScenarioState("real"); }, []);

  // §9 — repetir la demostración: reconstruye el fixture inicial EN MEMORIA (nunca toca backend).
  const resetDemo = useCallback(() => {
    if (scenario !== "real") setDemoState(initDemo(scenario));
  }, [scenario]);

  // Al cerrar sesión (o sin sesión) se vuelve SIEMPRE a REAL.
  // v2.13.3: los escenarios demo son herramienta del supervisor. Cualquier otra cuenta (monitorista
  // interno o institucional externo) opera SIEMPRE en datos reales, aunque llegue con estado previo.
  useEffect(() => {
    if (!user || user.actor_type === "external" || user.role !== "supervisor") resetToReal();
  }, [user, resetToReal]);

  // --- Mutadores DEMO locales (nunca al backend). Actualizan alarma (lista) + detalle (Día 1). ---
  const applyAction = useCallback((id, updater) => {
    setDemoState((prev) => {
      if (!prev) return prev;
      const next = clone(prev);
      const a = next.alarmas.find((x) => x.id === id);
      const d = next.detalles ? next.detalles[id] : null;
      const now = new Date().toISOString();
      // DEMO-002/DEMO-005: una acción NUEVA la ejecuta el USUARIO AUTENTICADO, así que lleva su
      // identidad. El actor del fixture (`next.actor`) sólo describe el HISTÓRICO precargado y se
      // conserva intacto; jamás se atribuye al personaje lo que acaba de hacer el operador.
      const actor = user ? { id: user.id, nombre: user.nombre || user.email } : next.actor;
      updater({ a, d, now, actor });
      if (d) refrescarDia1(d);          // siguiente_accion / permitidas / resultados / secuencia
      return next;
    });
  }, [user]);

  const pushSeq = (d, t) => { if (!d) return; if (!Array.isArray(d.transiciones)) d.transiciones = []; d.transiciones.push(t); };

  const demoTake = useCallback((id) => applyAction(id, ({ a, d, now, actor }) => {
    [a, d].forEach((o) => { if (!o) return; o.estado = "tomada"; o.operador_id = actor.id; o.operador_nombre = actor.nombre; o.tomada_en = now; });
    pushSeq(d, { estado: "tomada", usuario: actor.nombre, hora: now });
  }), [applyAction]);

  const demoTransition = useCallback((id, estado) => applyAction(id, ({ a, d, now, actor }) => {
    [a, d].forEach((o) => { if (o) o.estado = estado; });
    pushSeq(d, { estado, usuario: actor.nombre, hora: now });
  }), [applyAction]);

  const demoLiberar = useCallback((id, motivo) => applyAction(id, ({ a, d, now, actor }) => {
    [a, d].forEach((o) => { if (!o) return; o.estado = "recibida"; o.operador_id = null; o.operador_nombre = null; o.tomada_en = null; });
    pushSeq(d, { estado: "recibida", desde: "tomada", motivo_codigo: motivo || "otro", usuario: actor.nombre, hora: now });
  }), [applyAction]);

  const demoCerrar = useCallback((id, payload = {}) => applyAction(id, ({ a, d, now, actor }) => {
    [a, d].forEach((o) => {
      if (!o) return;
      o.estado = "cerrada"; o.clasificacion = payload.clasificacion || null; o.causa_falsa = payload.causa_falsa || null;
      o.nota_cierre = payload.nota_cierre || ""; o.cierre_en = now; o.cerrada_por_nombre = actor.nombre; o.cierre_actor_tipo = "operador";
    });
    pushSeq(d, { estado: "cerrada", clasificacion: payload.clasificacion || null, usuario: actor.nombre, hora: now });
  }), [applyAction]);

  const demoNota = useCallback((id, texto) => applyAction(id, ({ a, d, now, actor }) => {
    const nota = { texto, por: actor.nombre, en: now };
    [a, d].forEach((o) => { if (!o) return; if (!Array.isArray(o.notas_gestion)) o.notas_gestion = []; o.notas_gestion.push(nota); });
  }), [applyAction]);

  const demoStep = useCallback((id, orden) => applyAction(id, ({ a, d, now, actor }) => {
    [a, d].forEach((o) => {
      const p = (o && o.action_plan_congelado && o.action_plan_congelado.pasos || []).find((x) => x.orden === orden);
      if (p) { p.ejecutado = true; p.ejecutado_por = actor.nombre; p.ejecutado_en = now; }
    });
  }), [applyAction]);

  // Ciclo Día 1 en DEMO: cada control visible produce la MISMA consecuencia visible que en REAL,
  // resuelta en memoria (journal, desenlace, emergencia, gestiones y secuencia del caso).
  const demoAccion = useCallback((id, payload) => applyAction(id, ({ d, now, actor }) => {
    aplicarAccion(d, payload, actor, now);
  }), [applyAction]);

  const demoDesenlace = useCallback((id, payload) => applyAction(id, ({ d, now, actor }) => {
    aplicarDesenlace(d, payload, actor, now);
  }), [applyAction]);

  const demoGestion = useCallback((id, contactoId, payload) => applyAction(id, ({ d, now, actor }) => {
    aplicarGestion(d, contactoId, payload, actor, now);
  }), [applyAction]);

  const demoSeguimiento = useCallback((id, payload) => applyAction(id, ({ d, now, actor }) => {
    aplicarSeguimiento(d, payload, actor, now);
  }), [applyAction]);

  // Objeto demo vivo: fuente mutable + proyecciones derivadas (centroVista, poblacion, fichas, cartera).
  const demo = useMemo(() => {
    if (!demoState) return null;
    return { ...demoState, ...projectDemo(demoState) };
  }, [demoState]);

  const value = useMemo(() => ({
    scenario, setScenario, resetToReal, resetDemo, demo, isDemo: !!demo,
    demoTake, demoTransition, demoLiberar, demoCerrar, demoNota, demoStep,
    demoAccion, demoDesenlace, demoGestion, demoSeguimiento,
  }), [scenario, setScenario, resetToReal, resetDemo, demo, demoTake, demoTransition, demoLiberar, demoCerrar, demoNota, demoStep,
    demoAccion, demoDesenlace, demoGestion, demoSeguimiento]);

  return <ScenarioCtx.Provider value={value}>{children}</ScenarioCtx.Provider>;
}

export function useScenario() {
  const c = useContext(ScenarioCtx);
  return c || { scenario: "real", setScenario: () => {}, resetToReal: () => {}, resetDemo: () => {}, demo: null, isDemo: false,
    demoTake: () => {}, demoTransition: () => {}, demoLiberar: () => {}, demoCerrar: () => {}, demoNota: () => {}, demoStep: () => {},
    demoAccion: () => {}, demoDesenlace: () => {}, demoGestion: () => {}, demoSeguimiento: () => {} };
}
